import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getMerchantAPIUrl } from '../../services/apollo/urlResolver';
import * as Sentry from '@sentry/react';
import { fetchFeatureFlags as fetchFlags } from '@frontend/utils';
import type { CurrencyFeatureFlags } from '../utils';

export interface FeatureFlags extends CurrencyFeatureFlags {
  walletConnect: boolean | undefined;
  binancePay: boolean | undefined;
  /** Gates the OpenPayd partner disclosure shown on first visit. */
  openpaydIntegration: boolean | undefined;
}

interface FeatureFlagContext {
  featureFlags: FeatureFlags;
}

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  walletConnect: undefined,
  binancePay: undefined,
  widgetSolCurrency: undefined,
  widgetBnbCurrency: undefined,
  widgetBaseNetwork: undefined,
  widgetUsdcSolanaCurrency: undefined,
  widgetWalletConnectPay: undefined,
  openpaydIntegration: undefined,
};

const FeatureFlagContext = createContext<FeatureFlagContext>({
  featureFlags: DEFAULT_FEATURE_FLAGS,
});

const fetchFeatureFlags = async (): Promise<FeatureFlags> => {
  try {
    return await fetchFlags(getMerchantAPIUrl(), DEFAULT_FEATURE_FLAGS);
  } catch (error) {
    Sentry.withScope((scope: Sentry.Scope) => {
      scope.setExtra('errorType', (error as Error)?.name);
      scope.setExtra('errorMessage', (error as Error)?.message);
      scope.setTag('error_type', 'network_error');

      Sentry.captureException(error);
    });

    return DEFAULT_FEATURE_FLAGS;
  }
};

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    DEFAULT_FEATURE_FLAGS
  );

  useEffect(() => {
    const loadFeatureFlags = async () => {
      const flags = await fetchFeatureFlags();
      setFeatureFlags(flags);
    };

    loadFeatureFlags();
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ featureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = () => useContext(FeatureFlagContext);
